import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { MetricOptionProps } from "../SharedComponents/Interfaces/CustomCardWithMetrics.interfaces";

export interface IPartner {
  data: any;
  name: string;
  partnerOneID: any;
  mpnID: any;
  mpnName: any;
}

export interface IPartnerData {
  mpnId: string;
  mpnName: string;
  partnerOneID: string;
  partnerOneName: string;
  partnerCountry: string;
  isElite: boolean;
  isChannelPartner: boolean;
  isDefault: boolean;
  cspCustomersCount: number;
  cporCustomersCount: number;
  palCustomersCount: number;
  cspLoginMAU: number;
  cporLoginMAU: number;
  palLoginMAU: number;
}

export type IPartnerSearchDataGridProps = { searchText: string };

export enum IPartnerReducerType {
  LoadMenuItems,
  UpdateSelectedMenuItems
}

export interface IPartnerFilterPillDispatchProps {
  type: IPartnerReducerType;
  data: IContextualMenuItem[] | string[];
}

export type IPartnerProfile = {
  MPNId: any;
  MPNName: string;
  PartnerOneId: string;
  PartnerOneName: string;
  PartnerCountry: string;
  IsCPOR: boolean;
  IsCSP: boolean;
  IsPAL: boolean;
  CPORWorkloadsCount: number;
  CSPSubscriptionSKUCount: number;
  PALWorkloadsCount: number;
  CPORTenantCount?: number | null;
  CSPTenantCount?: number | null;
  PALTenantCount?: number | null;
};

export type IPartnerProfileResponse = {
  partnerProfileData: IPartnerProfile[];
  partnerProfileKustoLink: string;
  associationTypeKustoLink: string;
  partnerCountryKustoLink: string;
};

export type IPartnerProfileProps = {
  tableData: IPartnerProfile[];
  kustoLink: string;
  partnerOneName: string;
};

export interface ISelectedPartnerInformation {
  partnerInformation: ISelectedPartner;
  partnersList: Array<{ key: string; value: string }>;
}

export interface ISelectedPartner {
  partnerOneID?: string;
  partnerOneName?: string;
  mpnId?: string;
  mpnName?: string;
  partnerCountry?: string;
  isElite?: boolean;
  isCSP?: boolean;
  isCPOR?: boolean;
  isPAL?: boolean;
  cspLoginMAU?: number | null;
  cspTenantCount?: number | null;
  cporLoginMAU?: number | null;
  cporTenantCount?: number | null;
  palLoginMAU?: number | null;
  palTenantCount?: number | null;
}

export interface IPartnerContactProps {
  loading: boolean;
  partnerOneName: string;
}

export interface IPartnerContactsPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  partnerOneName: string;
}

export interface IPartnerDetailsProps {
  loading: boolean;
  Details?: {
    isElite?: boolean;
    isCSP?: boolean;
    isCPOR?: boolean;
    isPAL?: boolean;
    cspLoginMAU?: number | null;
    cspTenantCount?: number | null;
    cporLoginMAU?: number | null;
    cporTenantCount?: number | null;
    palLoginMAU?: number | null;
    palTenantCount?: number | null;
  };
  custDetails?: {
    hasPartner?: boolean;
  };
}

export interface IPartnerInfoProps {
  name: string;
  loaded: boolean;
  error: boolean;
  displaytext?: string;
}

export interface IPartnerInfoIdentityProps {
  loading: boolean;
  name: string;
  partnerOneID: string | null;
  mpnId: string | null;
  mpnName: string | null;
  error: any;
}

export interface IPartnerInfoIdentityDetailsProps {
  loading: boolean;
  name: string;
  partnerOneID: string | null;
  mpnId: string | null;
  mpnName: string | null;
}

export interface IPartnerPivotProps {
  partnerOneName: string;
  partnerOneID: string;
  mpnId: string;
}

export interface IPartnerProfileChartProps {
  readonly loading: boolean;
  readonly filteredData: IPartnerProfile[];
  readonly associationTypeKustoLink: string;
  readonly partnerCountryKustoLink: string;
  readonly onSelectedChartFilter: (filter: { filterKey: string; value: any }) => void;
}

export interface IPartnerContainerProps {
  partnerOneName: string;
  partnerOneID: string;
  mpnId: string;
}

export type IPartnerTenantDetailsResponse = {
  tenantDetails: IPartnerTenantDetails[];
  tenantDetailsKustoLink: string;
  partnerTypeTenantAssociationKustoLink: string;
  segmentGroupKustoLink: string;
  verticalKustoLink: string;
  industryKustoLink: string;
};

export type IPartnerTenantDetails = {
  PartnerOneId: string;
  MPNId: string;
  MPNName: string;
  PartnerAssociationType: string;
  TenantId: string;
  TenantName: string;
  SegmentGroup: string;
  SubSegment: string;
  IndustryName: string;
  VerticalName: string;
  LoginMAU: number | null;
};

export type IPartnerTenantDetailsProps = {
  tableData: IPartnerTenantDetails[];
  partnerOneName: string;
  kustoLink: string;
};

export interface IPartnerTenantDetailsChartProps {
  readonly loading: boolean;
  readonly filteredData: IPartnerTenantDetails[];
  readonly associationTypeKustoLink: string;
  readonly segementDistributionKustoLink: string;
  readonly industryKustoLink: string;
  readonly onSelectedChartFilter: (filter: { filterKey: string; value: any }) => void;
}

export interface IPartnerEngagementDetailsChartProps {
  readonly loading: boolean;
  readonly options: MetricOptionProps<any>;
  readonly onSelectedChartFilter: (filter: { filterKey: string; value: string }) => void;
}

export interface IPartnerProfileWorkloadPanelProps {
  workloadList: string[];
  partnerName: string;
}

export interface IPartnerFavoriteProps {
  partnerOneId: string;
  partnerOneName: string;
  isFavorite: boolean;
}

export type IPartnerSubscriptionDataResponse = {
  partnerSubscriptionChartsData: IPartnerSubscriptionCharts[];
  partnerSubscriptionTableData: IPartnerSubscriptionTable[];
  partnerSubscriptionTableDataKustoLink: string;
  latestMonthTenantsBySkuFamilyKustoLink: string;
  latestMonthTotalSeatsBySkuFamilyKustoLink: string;
  latestMonthTotalPAUBySkuFamilyKustoLink: string;
  tenantsTrendBySkuFamilyKustoLink: string;
  totalSeatsTrendBySkuFamilyKustoLink: string;
  totalPAUTrendBySkuFamilyKustoLink: string;
};

export type IPartnerSubscriptionCharts = {
  MPNId: string;
  SnapshotDate: Date;
  PartnerOneID: string;
  SkuFamily: string;
  TotalSeats: number;
  NumberOfTenants: number;
  PaidSeats: number;
};

export type IPartnerSubscriptionTable = {
  SubscriptionId: string;
  MPNId: number;
  MPNName: string;
  TenantId: string;
  TenantName: string;
  SkuName: string;
  SkuCategory: string;
  SkuAssigned: string;
  SubscriptionStatus: string;
  Tier1PartnerMPNId: number;
  Tier1PartnerMPNName: string;
  Tier2PartnerMPNId: number;
  Tier2PartnerMPNName: string;
  SubscriptionCreatedDate: Date;
  SubscriptionEndDate: Date;
  RenewalBucket: string;
  TotalSeats: number;
  PaidSeats: number;
};

export interface IPartnerSubscriptionChartProps {
  readonly loading: boolean;
  readonly filteredData: IPartnerSubscriptionCharts[];
  readonly latestMonthTenantsBySkuFamilyKustoLink: string;
  readonly latestMonthTotalSeatsBySkuFamilyKustoLink: string;
  readonly latestMonthTotalPAUBySkuFamilyKustoLink: string;
  readonly tenantsTrendBySkuFamilyKustoLink: string;
  readonly totalSeatsTrendBySkuFamilyKustoLink: string;
  readonly totalPAUTrendBySkuFamilyKustoLink: string;
  readonly onSelectedChartFilter: React.Dispatch<
    React.SetStateAction<{
      filterKey: string;
      value: any;
    }>
  >;
}

export interface PartnerSubscriptionTrendChartsProps {
  loading: boolean;
  metricFilters: Record<string, any>;
  onDataPointClick: (option: any) => void;
  onResetFilters: () => void;
  filteredData: any[];
  readonly tenantsTrendBySkuFamilyKustoLink: string;
  readonly totalSeatsTrendBySkuFamilyKustoLink: string;
  readonly totalPAUTrendBySkuFamilyKustoLink: string;
}

export type IPartnerSubscriptionTableProps = {
  tableData: IPartnerSubscriptionTable[];
  partnerOneName: string;
  kustoLink: string;
};

export type IPartnerEngagementsTableProps = {
  tableData: IPartnerEngagementsDetails[];
  partnerOneName: string;
  kustoLink: string;
};

export type IPartnerEngagementsDetails = {
  CRMURL: string;
  PartnerOneID: string;
  PartnerOneName: string;
  PartnerMPNId: string;
  Title: string;
  ProgramName: string;
  MeetingType: string;
  MeetingSubType: string;
  DateConfirmedStartTime: Date | null;
  DateConfirmedEndTime: Date | null;
  EngagementOwner: string;
  EngagementStatus: string;
  MSFTAttendeesConfirmed: string;
  EngagementId: string;
};
export type IPartnerEngagementsResponse = {
  partnerEngagementDetails: IPartnerEngagementsDetails[] | undefined;
  partnerEngagementDetailsKustoLink: string;
  engagementOwnerKustoLink: string;
  meetingTypeKustoLink: string;
  programNameKustoLink: string;
};

